import { z } from "zod";
import { nullableText, versionSchema } from "./common.js";

/** Audit events that can be subscribed to by an automation endpoint. */
export const WEBHOOK_EVENT_NAMES = [
  "attachment.deleted",
  "attachment.uploaded",
  "auth.email_change_requested",
  "auth.email_changed",
  "auth.locale_changed",
  "auth.login",
  "auth.login_failed",
  "auth.password_changed",
  "auth.password_reset",
  "auth.password_reset_requested",
  "automation.webhook_created",
  "automation.webhook_retry_requested",
  "automation.webhook_secret_rotated",
  "automation.webhook_updated",
  "catalog_item.created",
  "catalog_item.deactivated",
  "catalog_item.updated",
  "category.created",
  "category.updated",
  "company_holiday.created",
  "company_holiday.deleted",
  "company_holiday.updated",
  "customer.created",
  "customer.deleted",
  "customer.updated",
  "data.exported",
  "employee.balance_updated",
  "employee.offboarded",
  "employee.offboarding_cancelled",
  "employee.offboarding_scheduled",
  "employee.profile_updated",
  "employee.reactivated",
  "employee.schedule_updated",
  "employee_document.deleted",
  "employee_document.uploaded",
  "expense.ai_suggestion",
  "expense_claim.created",
  "expense_claim.deleted",
  "expense_claim.paid",
  "expense_claim.returned",
  "expense_claim.submitted",
  "expense_claim.updated",
  "expense_claim.withdrawn",
  "expense_receipt.deleted",
  "expense_receipt.uploaded",
  "inventory.adjusted",
  "inventory.issued",
  "inventory.received",
  "inventory.returned",
  "inventory.transferred",
  "invoice.cancelled",
  "invoice.created",
  "invoice.credit_note_created",
  "invoice.deleted",
  "invoice.issued",
  "invoice.public_download",
  "invoice.sent",
  "invoice.share_created",
  "invoice.share_revoked",
  "invoice.updated",
  "offer.accepted",
  "offer.created",
  "offer.declined",
  "offer.deleted",
  "offer.expired",
  "offer.revised",
  "offer.sent",
  "offer.updated",
  "organization.created",
  "organization.updated",
  "payment.deleted",
  "payment.recorded",
  "payment.updated",
  "project.budget_updated",
  "project.cost_created",
  "project.cost_deleted",
  "project.cost_updated",
  "project.created",
  "project.deleted",
  "project.members_replaced",
  "project.schedule_created",
  "project.schedule_deleted",
  "project.schedule_updated",
  "project.status_changed",
  "project.time_created",
  "project.time_deleted",
  "project.time_updated",
  "project.updated",
  "request.created",
  "request.deleted",
  "request.status_changed",
  "request.updated",
  "role.created",
  "role.deleted",
  "role.updated",
  "settings.billing_updated",
  "settings.company_updated",
  "settings.documents_updated",
  "settings.email_retried",
  "settings.localization_updated",
  "settings.logo_updated",
  "settings.numbering_updated",
  "settings.project_role_permissions_updated",
  "settings.work_updated",
  "setup.completed",
  "system_admin.api_key_created",
  "system_admin.api_key_revoked",
  "system_admin.impersonation_ended",
  "system_admin.impersonation_started",
  "tax_rate.created",
  "tax_rate.deleted",
  "time.allocation_cell_updated",
  "time.clock_in",
  "time.clock_out",
  "time.entry_created",
  "time.entry_deleted",
  "time.entry_updated",
  "timesheet.approve",
  "timesheet.reject",
  "timesheet.reopen",
  "timesheet.submit",
  "timesheet.withdraw",
  "unit.created",
  "unit.updated",
  "user.activated",
  "user.deactivated",
  "user.email_change_requested",
  "user.email_changed",
  "user.invited",
  "user.password_reset_requested",
  "user.updated",
] as const;
export type WebhookEventName = (typeof WEBHOOK_EVENT_NAMES)[number];
export const WEBHOOK_EVENT_PATTERNS = ["*", ...WEBHOOK_EVENT_NAMES] as const;
export type WebhookEventPattern = (typeof WEBHOOK_EVENT_PATTERNS)[number];

const webhookUrlSchema = z
  .string()
  .trim()
  .url()
  .max(2048)
  .refine((value) => {
    const protocol = new URL(value).protocol;
    return protocol === "http:" || protocol === "https:";
  }, "Webhook URL must use HTTP or HTTPS");

const webhookEndpointFieldsSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    url: webhookUrlSchema,
    events: z.array(z.enum(WEBHOOK_EVENT_PATTERNS)).min(1).max(100),
    active: z.boolean().default(true),
  })
  .strict();

export const webhookEndpointCreateSchema =
  webhookEndpointFieldsSchema.transform((value) => ({
    ...value,
    events: [...new Set(value.events)],
  }));
export type WebhookEndpointCreateDto = z.infer<
  typeof webhookEndpointCreateSchema
>;
export const webhookCreateSchema = webhookEndpointCreateSchema;

export const webhookEndpointUpdateSchema = webhookEndpointFieldsSchema
  .partial()
  .extend({
    version: versionSchema,
    active: z.boolean().optional(),
  })
  .strict()
  .transform((value) => ({
    ...value,
    ...(value.events ? { events: [...new Set(value.events)] } : {}),
  }));
export type WebhookEndpointUpdateDto = z.infer<
  typeof webhookEndpointUpdateSchema
>;

export const webhookEndpointRotateSecretSchema = z
  .object({ version: versionSchema })
  .strict();
export type WebhookEndpointRotateSecretDto = z.infer<
  typeof webhookEndpointRotateSecretSchema
>;
export const webhookRotateSecretSchema = webhookEndpointRotateSecretSchema;

export const webhookEndpointTestSchema = z
  .object({ version: versionSchema })
  .strict();
export type WebhookEndpointTestDto = z.infer<typeof webhookEndpointTestSchema>;

export const webhookDeliveryRetrySchema = z
  .object({ version: versionSchema })
  .strict();
export type WebhookDeliveryRetryDto = z.infer<
  typeof webhookDeliveryRetrySchema
>;

export const WEBHOOK_DELIVERY_STATUSES = [
  "pending",
  "delivered",
  "failed",
] as const;
export type WebhookDeliveryStatus = (typeof WEBHOOK_DELIVERY_STATUSES)[number];

export const webhookDeliveryResponseSchema = z
  .object({
    id: z.string().uuid(),
    eventName: z.enum(WEBHOOK_EVENT_NAMES),
    auditId: z.string().uuid(),
    status: z.enum(WEBHOOK_DELIVERY_STATUSES),
    attemptCount: z.number().int().min(0),
    nextAttemptAt: z.string().datetime().nullable(),
    responseStatus: z.number().int().min(100).max(599).nullable(),
    responseBody: nullableText(4000),
    deliveredAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();
export type WebhookDeliveryResponseDto = z.infer<
  typeof webhookDeliveryResponseSchema
>;
